// Add Folder Dialog Component
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Folder } from 'lucide-react';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import Button from '@/components/Button';
import Input from '@/components/Input';

interface AddFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FOLDER_COLORS = [
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#10b981' },
  { name: 'Yellow', value: '#f59e0b' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Gray', value: '#6b7280' },
];

export default function AddFolderDialog({ open, onOpenChange }: AddFolderDialogProps) {
  const [name, setName] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [selectedColor, setSelectedColor] = useState('#6366f1');
  const [error, setError] = useState('');

  const queryClient = useQueryClient();

  // Get folders for parent selection
  const { data: foldersData } = useQuery({
    queryKey: ['folders'],
    queryFn: () => api.getFolders(),
  });

  const folders = foldersData?.folders || [];

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: (data: { name: string; parent_folder_id?: number | null; color?: string }) =>
      api.createFolder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      setName('');
      setSelectedParentId(null);
      setSelectedColor('#6366f1');
      setError('');
      onOpenChange(false);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to create folder');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter a folder name');
      return;
    }

    if (name.length > 100) {
      setError('Folder name must be 100 characters or less');
      return;
    }

    createFolderMutation.mutate({
      name: name.trim(),
      parent_folder_id: selectedParentId,
      color: selectedColor,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Folder className="h-5 w-5 text-primary" />
            <DialogTitle>Create Folder</DialogTitle>
          </div>
          <DialogDescription>
            Organize your feeds by creating a new folder
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="folder-name" className="text-sm font-medium">
              Folder Name
            </label>
            <Input
              id="folder-name"
              type="text"
              placeholder="e.g., Tech News, Blogs, etc."
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={createFolderMutation.isPending}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="parent-folder" className="text-sm font-medium">
              Parent Folder (optional)
            </label>
            <select
              id="parent-folder"
              value={selectedParentId || ''}
              onChange={(e) => setSelectedParentId(e.target.value ? Number(e.target.value) : null)}
              disabled={createFolderMutation.isPending}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">None (top level)</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Color</label>
            <div className="flex flex-wrap gap-2">
              {FOLDER_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setSelectedColor(color.value)}
                  disabled={createFolderMutation.isPending}
                  className={`h-8 w-8 rounded-md border-2 transition-all ${
                    selectedColor === color.value
                      ? 'border-foreground scale-110'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createFolderMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createFolderMutation.isPending}>
              {createFolderMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Folder'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
